import type { Permission } from '@/config/permissions';
import { useUserStore } from '@/stores/user';
import { hasPermission as routeHasPermission } from '@/utils/permission';
import type { RouteLocationNormalized } from 'vue-router';

export interface ComplexPermission {
  any?: Permission[];
  all?: Permission[];
}

function matchAny(perms: Permission[], owned: Permission[]) {
  return perms.some((p) => owned.includes(p));
}
function matchAll(perms: Permission[], owned: Permission[]) {
  return perms.every((p) => owned.includes(p));
}

export function usePermission() {
  const userStore = useUserStore();
  const owned = userStore.permissions as Permission[];

  function has(p: Permission | Permission[]) {
    return Array.isArray(p) ? matchAny(p, owned) : owned.includes(p);
  }

  function hasAll(p: Permission[]) {
    return matchAll(p, owned);
  }

  function complex(cp: ComplexPermission) {
    const anyOk = !cp.any || matchAny(cp.any, owned);
    const allOk = !cp.all || matchAll(cp.all, owned);
    return anyOk && allOk;
  }

  function canAccessRoute(route: RouteLocationNormalized) {
    return routeHasPermission(route);
  }

  // 别名：简化语义（与原 useHasPerm 保持兼容）
  const hasPerm = (perm: Permission) => has(perm);
  const hasAnyPerm = (perms: Permission[] | Permission) => has(perms);
  const hasAllPerm = (perms: Permission[] | Permission) =>
    hasAll(Array.isArray(perms) ? perms : [perms]);

  return { has, hasAll, complex, canAccessRoute, hasPerm, hasAnyPerm, hasAllPerm };
}
